import React, { useRef } from 'react';
import classNames from 'classnames';

import { ChartTooltip, formatShare, useHoveredSegment } from '../chart-tooltip';
import { useFitTextScale } from './useFitTextScale';

export interface DonutSegment {
  key: string;
  label: string;
  value: number;
  color: string;
}

export interface DonutProps {
  /** Drawn clockwise from 12 o'clock in order. Zero or negative values are skipped (an arc cannot be negative). */
  segments: DonutSegment[];
  /**
   * A second lap for what is beyond the full ring (e.g. over Allocated): drawn clockwise from 12 o'clock on a thinner
   * ring just outside the first, each value a share of the same full ring, capped at one lap.
   */
  overSegments?: DonutSegment[];
  /** What a full ring represents. Defaults to the sum of the positive segment values. */
  total?: number;
  /** Large centre text, e.g. "$2,600". */
  centreValue?: React.ReactNode;
  /** Small centre text under it, e.g. "Allocated". */
  centreLabel?: React.ReactNode;
  /** Required: the chart's accessible name (role="img"). */
  ariaLabel: string;
  /**
   * What the hover percentage is a share of, e.g. Allocated. Defaults to the ring's total. Pass a figure other than the
   * ring when the ring can run past it, so a segment beyond it reads above 100% in sum.
   */
  percentBase?: number;
  /** Shows the segment's label and percentage beside the pointer on hover. Default true. */
  showTooltip?: boolean;
  /** Rendered size in px (square). Default 200. */
  size?: number;
  /** Ring thickness in viewBox units (viewBox is 200). Default 30. */
  thickness?: number;
  className?: string;
  dataCy?: string;
}

const CENTRE = 100;
const RADIUS = 72;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
/** The second lap: its thickness, and the clear gap between it and the first ring, in viewBox units. */
const LAP_THICKNESS = 8;
const LAP_GAP = 2;
/** Clear space kept between the centre text and the ring, each side, in viewBox units. */
const TEXT_INSET = 6;

/**
 * A plain SVG donut: arcs from `stroke-dasharray`, an optional second lap for what is over the ring, optional centre
 * text, and a percentage tooltip on hover. The centre value and label shrink to fit inside the ring's hole when they
 * are too wide for it (e.g. "$1,234,567"), so they never run into the ring.
 */
const Donut: React.FC<DonutProps> = ({
  segments,
  overSegments = [],
  total,
  centreValue,
  centreLabel,
  ariaLabel,
  percentBase,
  showTooltip = true,
  size = 200,
  thickness = 30,
  className,
  dataCy = 'donut',
}) => {
  const { hovered, onMove, onLeave } = useHoveredSegment();
  const valueRef = useRef<SVGTextElement>(null);
  const labelRef = useRef<SVGTextElement>(null);
  // The hole's width, less clear space each side
  const textWidth = 2 * (RADIUS - thickness / 2) - 2 * TEXT_INSET;
  const valueScale = useFitTextScale(valueRef, textWidth, centreValue);
  const labelScale = useFitTextScale(labelRef, textWidth, centreLabel);
  const positive = segments.filter(({ value }) => value > 0);
  const ringTotal = total ?? positive.reduce((sum, { value }) => sum + value, 0);
  const base = percentBase !== undefined && percentBase > 0 ? percentBase : ringTotal;

  const positiveOver = overSegments.filter(({ value }) => value > 0);
  const lapRadius = RADIUS + thickness / 2 + LAP_GAP + LAP_THICKNESS / 2;
  const lapCircumference = 2 * Math.PI * lapRadius;
  // One lap is the full ring's total; with an empty ring the second lap's own total is the lap
  const lapTotal = ringTotal > 0 ? ringTotal : positiveOver.reduce((sum, { value }) => sum + value, 0);

  let lapOffset = 0;
  const lapArcs = positiveOver.map(({ key, value, color }) => {
    const length = Math.min((value / lapTotal) * lapCircumference, lapCircumference - lapOffset);
    const arc = (
      <circle
        key={key}
        data-key={key}
        className={classNames('donut-arc', 'donut-arc--lap', { hovered: hovered?.key === key })}
        cx={CENTRE}
        cy={CENTRE}
        r={lapRadius}
        fill="none"
        stroke={color}
        strokeWidth={LAP_THICKNESS}
        strokeDasharray={`${length} ${lapCircumference}`}
        strokeDashoffset={-lapOffset}
        onMouseMove={showTooltip ? (event) => onMove(key, event) : undefined}
        onMouseLeave={showTooltip ? onLeave : undefined}
      />
    );
    lapOffset += length;
    return arc;
  });

  let offset = 0;
  const arcs =
    ringTotal > 0
      ? positive.map(({ key, value, color }) => {
          const length = (value / ringTotal) * CIRCUMFERENCE;
          const arc = (
            <circle
              key={key}
              data-key={key}
              className={classNames('donut-arc', { hovered: hovered?.key === key })}
              cx={CENTRE}
              cy={CENTRE}
              r={RADIUS}
              fill="none"
              stroke={color}
              strokeWidth={thickness}
              strokeDasharray={`${length} ${CIRCUMFERENCE}`}
              strokeDashoffset={-offset}
              onMouseMove={showTooltip ? (event) => onMove(key, event) : undefined}
              onMouseLeave={showTooltip ? onLeave : undefined}
            />
          );
          offset += length;
          return arc;
        })
      : [];

  const hoveredSegment = hovered && [...positive, ...positiveOver].find(({ key }) => key === hovered.key);

  return (
    <div className={classNames('donut-wrapper', className)} style={{ width: size, height: size }}>
      <svg
        viewBox="0 0 200 200"
        width={size}
        height={size}
        role="img"
        aria-label={ariaLabel}
        className="donut"
        data-cy={dataCy}
      >
        <circle className="donut-track" cx={CENTRE} cy={CENTRE} r={RADIUS} fill="none" strokeWidth={thickness} />
        <g transform={`rotate(-90 ${CENTRE} ${CENTRE})`}>
          {arcs}
          {lapArcs}
        </g>
        {centreValue !== undefined && (
          <text
            ref={valueRef}
            className="donut-centre-value"
            x={CENTRE}
            y={102}
            textAnchor="middle"
            style={valueScale < 1 ? { fontSize: `${26 * valueScale}px` } : undefined}
          >
            {centreValue}
          </text>
        )}
        {centreLabel !== undefined && (
          <text
            ref={labelRef}
            className="donut-centre-label"
            x={CENTRE}
            y={124}
            textAnchor="middle"
            style={labelScale < 1 ? { fontSize: `${14 * labelScale}px` } : undefined}
          >
            {centreLabel}
          </text>
        )}
      </svg>
      {hovered && hoveredSegment && base > 0 && (
        <ChartTooltip
          x={hovered.x}
          y={hovered.y}
          label={hoveredSegment.label}
          value={formatShare(hoveredSegment.value, base)}
          color={hoveredSegment.color}
          dataCy={`${dataCy}-tooltip`}
        />
      )}
    </div>
  );
};

export default Donut;
